import { Component, inject, Input, OnInit } from '@angular/core';

import { ComponentActions, ComponentType, ElementsType } from '../../../state-management/model/enums/state.enums';
import { WebsiteStateService } from '../../../state-management/services/website-state.service';
import { _componentConfig } from './configuration.constants';

import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';

import { v4 as uuidv4 } from 'uuid';
import { firstValueFrom } from 'rxjs';
import { GenericDialog, DialogData } from '../../../components/meta/website-config/website-config-form/website-config-form-page-administration/generic-dialog/generic-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-configuration',
  imports: [
    MatIcon,
    MatMenuModule,
    MatButtonModule,
  ],
  templateUrl: './configuration.component.html',
  styleUrl: './configuration.component.scss'
})
export class ConfigurationComponent implements OnInit {
  @Input() cID!: string;
  @Input() sID!: string;
  @Input() cType!: ComponentType;

  public canConfigure!: boolean;
  public canDelete!: boolean;
  public canModifyGroupedElements!: boolean;
  public GroupedElementsLabel!: ElementsType | undefined;
  public LabelDisplay!: string;
  public addLabel!: string;
  public removeLabel!: string;
  public addIcon!: string;
  public removeIcon!: string;
  public hasAction!: {
    label: string;
    action: ComponentActions;
    icon: string;
  } | undefined;


  constructor(private dialog: MatDialog) { }


  ngOnInit(): void {
    this.canConfigure = _componentConfig[this.cType]?.canConfigure ?? false;
    this.canDelete = _componentConfig[this.cType]?.canDelete ?? false;
    this.canModifyGroupedElements = _componentConfig[this.cType]?.canModifyGroupedElements ?? false;
    this.GroupedElementsLabel = _componentConfig[this.cType]?.GroupedElementsLabel;
    this.LabelDisplay = _componentConfig[this.cType]?.LabelDisplay ?? 'Element';
    this.addLabel = _componentConfig[this.cType]?.addLabel ?? 'Element hinzufügen';
    this.removeLabel = _componentConfig[this.cType]?.removeLabel ?? 'Element löschen';
    this.addIcon = _componentConfig[this.cType]?.addIcon ?? 'add';
    this.removeIcon = _componentConfig[this.cType]?.removeIcon ?? 'delete';
    this.hasAction = _componentConfig[this.cType]?.hasAction ?? undefined;
  }

  private stateService = inject(WebsiteStateService);

  public triggerAction(action: ComponentActions): void {
    this.stateService.actions = [action];
  }

  public async removeComponent(): Promise<void> {
    const confirmDelete = await this.openDeletePageDialog();
    if (confirmDelete) {
      this.stateService.removeComponent(this.cID, this.sID)
    }

  }

  public removeGroupedElementFromComponent(): void {
    if (!this.GroupedElementsLabel) return;

    let groupedElementIds = this.stateService.getGroupComposedElementIds(this.cID, this.GroupedElementsLabel);
    groupedElementIds()[groupedElementIds().length - 1].map((elementId: string) => {
      this.stateService.removeElement(elementId, this.cID);
    });
  }

  public addGroupedElementToComponent(): void {
    const newBaseCardTemplate = this.createGroupedElementTemplate();
    if (!newBaseCardTemplate) return;

    Object.keys(newBaseCardTemplate).forEach((key) => {
      this.stateService.addElementToComponent(key, newBaseCardTemplate[key], this.cID);
    });
  }


  private openDeletePageDialog(): Promise<boolean> {
    const data: DialogData = {
      title: 'Warnung',
      textChunks: [
        { text: 'Soll diese Komponente endgültig gelöscht werden? Nachdem die Komponente gelöscht wurde, kann sie nicht mehr wiederhergestellt werden' }
      ],
      cancelButtonText: 'Abbrechen',
      confirmButtonText: 'Ja, löschen!',
    };


    const dialogRef = this.dialog.open(GenericDialog, {
      data: data,
      panelClass: "admin-theme"
    });
    return firstValueFrom(dialogRef.afterClosed());
  }



  
  private createGroupedElementTemplate(): Record<string, any> | undefined {

  if (!this.GroupedElementsLabel) return undefined;

  // calculate the max order of the elements in the component with cID
  let elements = this.stateService.getGroupComposedElementIds(this.cID, this.GroupedElementsLabel);
  let maxOrder = 0;
  elements().forEach((elementIds: string[]) => {
    let order = this.stateService.elements()[elementIds[elementIds.length - 1]].order;
    if (order && order > maxOrder) {
      maxOrder = order;
    }
  });
  maxOrder += 1;

  switch (this.GroupedElementsLabel) {
    case ElementsType.BaseCard:
      return this._generateCardTemplate(maxOrder);
    case ElementsType.ComponentImageText:
      return this._generateCompImageTextTemplate(maxOrder);
    default:
      return undefined;
  }
}



  // generate empty composed elements
  private _generateCompImageTextTemplate(maxOrder: number): Record < string, any > {
  const titleUID = uuidv4();
  const descriptionUID = uuidv4();
  const imageUID = uuidv4();

  return {
    [titleUID]: {
      "type": "comp-image-text",
      "value": "Titel des Elements.",
      "tag": "span",
      "id": titleUID,
      "styles": null,
      "label": "title",
      "order": maxOrder
    },
    [descriptionUID]: {
      "type": "comp-image-text",
      "value": "Beschreibung des Elements",
      "tag": "span",
      "id": descriptionUID,
      "styles": null,
      "label": "description",
      "order": maxOrder
    },
    [imageUID]: {
      "type": "comp-image-text",
      "value": null,
      "tag": "img",
      "id": imageUID,
      "styles": null,
      "label": "image",
      "order": maxOrder,
      "options": {
        "altText": "Beschreibung des Bildes"
      }
    }
  }
}
  
  private _generateCardTemplate(maxOrder: number): Record < string, any > {
  const titleUID = uuidv4();
  const descriptionUID = uuidv4();
  const imageUID = uuidv4();

  return {
    [titleUID]: {
      "type": "base-card",
      "value": "Titel",
      "tag": "span",
      "id": titleUID,
      "styles": null,
      "label": "title",
      "order": maxOrder
    },
    [descriptionUID]: {
      "type": "base-card",
      "value": "Beschreibung.",
      "tag": "span",
      "id": descriptionUID,
      "styles": null,
      "label": "description",
      "order": maxOrder
    },
    [imageUID]: {
      "type": "base-card",
      "value": null,
      "tag": "img",
      "id": imageUID,
      "styles": null,
      "label": "image",
      "order": maxOrder,
      "options": {
        "altText": "Beschreibung des Bildes"
      },
      "buttonConfig": {
        "type": "navigate",
        "params": {
          "url": "service/trauer"
        }
      }
    }
  }
}

}
